import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLoaderData, useSearchParams } from "react-router-dom";
import { Autocomplete, TextField, Pagination, CircularProgress } from "@mui/material";
import { useFirebase } from "../../../contexts/FirebaseContext";
import { fetchWorldCupsCount, loadWorldCupsPage } from "../../functions/worldBrowseFilters";
import { getAllWorldTracks } from "../../functions/worldTrackFilters";
import Cup from "../../components/Cup";
import "../../../shared/pages/Browse/SharedBrowse.css";
import "./WorldBrowse.css";

export default function WorldBrowse() {
    const [cups, setCups] = useState([]);
    const data = useLoaderData();
    const { firestore } = useFirebase();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get all available tracks for the autocomplete
    const allTracks = useMemo(() => getAllWorldTracks(), []);

    // Initialize filter states from URL params
    const getInitialTracks = () => {
        const tracksParam = searchParams.get('tracks');
        if (!tracksParam) return [];
        const trackIds = tracksParam.split(',');
        return allTracks.filter(track => trackIds.includes(track.id));
    };

    const getInitialPage = () => {
        const page = parseInt(searchParams.get('page'));
        return page > 0 ? page : 1;
    };

    const getInitialPageSize = () => {
        const size = parseInt(searchParams.get('pageSize'));
        return [12, 24, 48, 96].includes(size) ? size : 24;
    };

    const getInitialSort = () => {
        const sort = searchParams.get('sort');
        return sort === 'oldest' ? 'oldest' : 'newest';
    };

    // Filter states
    const [selectedTracks, setSelectedTracks] = useState(getInitialTracks());
    const [sortOrder, setSortOrder] = useState(getInitialSort());
    const [currentPage, setCurrentPage] = useState(getInitialPage());
    const [loading, setLoading] = useState(false);
    const [cupsPerPage, setCupsPerPage] = useState(getInitialPageSize());

    // Track total count and pagination
    const [totalCount, setTotalCount] = useState(data?.length || 0);
    const [filteredCount, setFilteredCount] = useState(0);
    const [clientFiltered, setClientFiltered] = useState(false);
    const [lastVisible, setLastVisible] = useState(null);

    // Update URL params when filters change
    const updateURLParams = useCallback((tracks, sort, page, pageSize) => {
        const params = {};
        
        if (tracks.length > 0) {
            params.tracks = tracks.map(t => t.id).join(',');
        }
        
        if (sort !== 'newest') {
            params.sort = sort;
        }
        
        if (page > 1) {
            params.page = page.toString();
        }
        
        if (pageSize !== 24) {
            params.pageSize = pageSize.toString();
        }
        
        setSearchParams(params, { replace: true });
    }, [setSearchParams]);

    // Fetch total count based on current filters
    const fetchTotalCount = useCallback(async () => {
        try {
            const count = await fetchWorldCupsCount(firestore, { selectedTracks, sortOrder });
            setTotalCount(count);
        } catch (error) {
            console.error('Error fetching count:', error);
        }
    }, [firestore, selectedTracks, sortOrder]);

    // Load cups for current page
    const loadCups = useCallback(async (pageNum = 1, resetLastVisible = false) => {
        setLoading(true);
        
        try {
            const currentLastVisible = resetLastVisible ? null : lastVisible;
            const result = await loadWorldCupsPage(
                firestore,
                { selectedTracks, sortOrder },
                { cupsPerPage, lastVisible: currentLastVisible, pageNum }
            );
            
            setCups(result.cups || []);
            setLastVisible(result.lastVisible);
            setClientFiltered(result.clientFiltered);
            setFilteredCount(result.filteredCount || 0);
        } catch (error) {
            console.error('Error loading cups:', error);
            setCups([]);
            setFilteredCount(0);
        } finally {
            setLoading(false);
        }
    }, [firestore, selectedTracks, sortOrder, lastVisible, cupsPerPage]);

    // Initialize with loader data and fetch actual count
    useEffect(() => {
        setCups(data);
        setTotalCount(data?.length || 0);
        fetchTotalCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    // Reload when filters or page size change
    useEffect(() => {
        setCurrentPage(1);
        setLastVisible(null);
        loadCups(1, true);
        fetchTotalCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTracks, sortOrder, cupsPerPage]);
    
    // Load new page when page changes
    useEffect(() => {
        if (currentPage > 1) {
            loadCups(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Pagination
    const displayCount = clientFiltered ? filteredCount : totalCount;
    const totalPages = Math.ceil(displayCount / cupsPerPage);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
        updateURLParams(selectedTracks, sortOrder, value, cupsPerPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleTrackChange = (event, newValue) => {
        if (newValue.length <= 4) {
            setSelectedTracks(newValue);
            updateURLParams(newValue, sortOrder, 1, cupsPerPage);
        }
    };

    const handleSortChange = (event, newValue) => {
        if (newValue) {
            setSortOrder(newValue);
            updateURLParams(selectedTracks, newValue, 1, cupsPerPage);
        }
    };

    const handlePageSizeChange = (event, newValue) => {
        if (newValue) {
            setCupsPerPage(newValue);
            updateURLParams(selectedTracks, sortOrder, 1, newValue);
        }
    };

    return (
        <div id="main-container">
            {/* <h1>Browse Cups</h1> */}
            
            <div className="filter-container">
                <Autocomplete
                    multiple
                    id="track-filter"
                    options={allTracks}
                    getOptionLabel={(option) => option.name}
                    value={selectedTracks}
                    onChange={handleTrackChange}
                    renderInput={(params) => (
                        <TextField {...params} label="Tracks (all selected)" variant="outlined" />
                    )}
                    sx={{
                        minWidth: 200,
                        flex: 1,
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'white',
                            },
                            '&:hover fieldset': {
                                borderColor: 'white',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'white',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: 'white',
                        },
                        '& .MuiChip-root': {
                            backgroundColor: '#555',
                            color: 'white',
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'white',
                        },
                    }}
                />

                <Autocomplete
                    id="sort-order"
                    options={['newest', 'oldest']}
                    getOptionLabel={(option) => option === 'newest' ? 'Newest First' : 'Oldest First'}
                    value={sortOrder}
                    onChange={handleSortChange}
                    renderInput={(params) => (
                        <TextField {...params} label="Sort By" variant="outlined" />
                    )}
                    sx={{
                        minWidth: 140,
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'white',
                            },
                            '&:hover fieldset': {
                                borderColor: 'white',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'white',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: 'white',
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'white',
                        },
                    }}
                />

                <Autocomplete
                    id="page-size"
                    options={[12, 24, 48, 96]}
                    getOptionLabel={(option) => option.toString()}
                    value={cupsPerPage}
                    onChange={handlePageSizeChange}
                    renderInput={(params) => (
                        <TextField {...params} label="Per Page" variant="outlined" />
                    )}
                    sx={{
                        minWidth: 120,
                        '& .MuiOutlinedInput-root': {
                            color: 'white',
                            '& fieldset': {
                                borderColor: 'white',
                            },
                            '&:hover fieldset': {
                                borderColor: 'white',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: 'white',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: 'white',
                        },
                        '& .MuiSvgIcon-root': {
                            color: 'white',
                        },
                    }}
                />
            </div>

            <p className="cup-count">
                Showing {cups.length} of {displayCount} cups (Page {currentPage} of {totalPages})
            </p>
            
            {cups.length === 0 ? (
                loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                        <CircularProgress sx={{ color: 'white' }} />
                    </div>
                ) : (
                    <div className="no-cups">
                        <p>No cups found. Be the first to create one!</p>
                        <Link to="/">Create a Cup</Link>
                    </div>
                )
            ) : (
                <div className="cups-grid">
                    {cups.map((cup) => (
                        <Link 
                            key={cup.id} 
                            to={`/browse/cup/${cup.id}`}
                            className="cup-card"
                        >
                            <div className="cup-preview">
                                <h3>{cup.cupName || "Untitled Cup"}</h3>
                                <div className="cup-mini">
                                    <Cup tracks={cup.tracks} />
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {loading && cups.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', margin: '20px 0' }}>
                    <CircularProgress sx={{ color: 'white' }} />
                </div>
            )}

            {totalPages > 1 && (
                <div className="pagination-container">
                    <Pagination 
                        count={totalPages} 
                        page={currentPage} 
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: 'white',
                            },
                            '& .Mui-selected': {
                                backgroundColor: '#555 !important',
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
}

